/**
 * Federated-Photos URL helpers — internal implementation for `<FederatedPhoto>`
 * and the next/image custom loader at `@/src/lib/image/federatedPhotoLoader.ts`.
 *
 * Federated Photos render URLs follow:
 *   `<environmentHost>/fp/<photoKey>-<photoTreatment>.<extension>`
 *
 * This module converts a single Federated Photo URL from the API into a set of
 * responsive render URLs (`src` + `srcSet` + `placeholder`) using approved
 * treatment families. It stays on the same render host as the source URL so
 * dev / test / prod environments don't get crossed.
 *
 * The cc_ft_* treatments provide a 4:3 aspect ratio with center-crop "cover"
 * behaviour. `uncropped_scaled_within_*` keeps the source aspect ratio for
 * lightbox-style media. Custom treatments can be plugged in once Federated
 * Photos provisions HotPads-specific names (e.g. `hotpads_home_carousel_500_333`).
 *
 * The pure URL primitives (`extractFederatedPhotoInfoFromUrl`,
 * `buildFederatedPhotoTreatmentUrl`, `buildPresetTreatmentName`) are exported so the
 * next/image custom loader can build URLs without dragging in React.
 */

type FederatedPhotoWidth = 192 | 384 | 576 | 768 | 960 | 1152 | 1536;

/** Widths supported by the `cc_ft_*` treatment family. */
export type CcFtSize = FederatedPhotoWidth;

/** All Federated Photo render widths in ascending order; loader uses this to snap arbitrary widths to a supported variant. */
export const FED_PHOTO_WIDTHS: ReadonlyArray<FederatedPhotoWidth> = [192, 384, 576, 768, 960, 1152, 1536];

type FederatedPhotoTreatmentFamily = 'cc_ft' | 'uncropped_scaled_within';

type FederatedPhotoTreatmentVariant = {
  width: number;
  treatment: string;
};

type FederatedPhotoCustomTreatment = {
  defaultTreatment: string;
  srcSetTreatments?: Array<FederatedPhotoTreatmentVariant>;
  placeholderTreatment?: string;
};

/**
 * Either a preset treatment family (`'cc_ft' | 'uncropped_scaled_within'`) or
 * a custom set of named treatments + per-width srcset variants.
 */
export type FederatedPhotoTreatment = FederatedPhotoTreatmentFamily | FederatedPhotoCustomTreatment;

type ImageFormat = 'webp' | 'jpg';

type FederatedPhotoInfo = {
  photoKey: string;
  origin: string;
};

type BuildFederatedPhotoUrlOptions = {
  ext?: ImageFormat;
  origin?: string;
  treatment?: FederatedPhotoTreatment;
};

type BuildFederatedPhotoTreatmentUrlOptions = {
  ext?: ImageFormat;
  origin?: string;
};

type BuildFederatedPhotoAttrsOptions = {
  ext?: ImageFormat;
  treatment?: FederatedPhotoTreatment;
};

const FED_PHOTO_HOSTS = new Set(['photos.dev.zillow.net', 'photos.test.zillow.net', 'photos.zillowstatic.com']);
const FED_PHOTO_PROD_ORIGIN = 'https://photos.zillowstatic.com';
const PHOTO_KEY_RE = /^[a-f0-9]{32}$/;
const SCALED_WITHIN_HEIGHT_BY_WIDTH: Record<FederatedPhotoWidth, number> = {
  192: 144,
  384: 288,
  576: 432,
  768: 576,
  960: 720,
  1152: 864,
  1536: 1152,
};

export function isCustomFederatedPhotoTreatment(
  treatment: FederatedPhotoTreatment,
): treatment is FederatedPhotoCustomTreatment {
  return typeof treatment !== 'string';
}

export function buildPresetTreatmentName(treatment: FederatedPhotoTreatmentFamily, width: FederatedPhotoWidth): string {
  return treatment === 'uncropped_scaled_within'
    ? `uncropped_scaled_within_${width}_${SCALED_WITHIN_HEIGHT_BY_WIDTH[width]}`
    : `cc_ft_${width}`;
}

/**
 * Parses an API photo URL and returns the photoKey + render origin if the URL
 * is a documented Federated Photo render host. Returns null for anything else so
 * callers fall back to the original src.
 */
export function extractFederatedPhotoInfoFromUrl(url: string): FederatedPhotoInfo | null {
  try {
    const u = new URL(url);
    if (!FED_PHOTO_HOSTS.has(u.hostname)) return null;

    const fp = u.pathname.split('/fp/')[1];
    if (!fp) return null;
    const [photoKey] = fp.split('-');
    return PHOTO_KEY_RE.test(photoKey) ? { photoKey, origin: u.origin } : null;
  } catch {
    return null;
  }
}

export function buildFederatedPhotoTreatmentUrl(
  photoKey: string,
  treatmentName: string,
  options: BuildFederatedPhotoTreatmentUrlOptions = {},
): string {
  const { ext = 'webp', origin = FED_PHOTO_PROD_ORIGIN } = options;
  return `${origin}/fp/${photoKey}-${treatmentName}.${ext}`;
}

/**
 * Social-card render width: a large `cc_ft` rendition so scrapers (Facebook,
 * Slack, iMessage, Twitter) show a full-size share preview rather than a
 * thumbnail (HP-8404). 4:3 center-crop, so the height is `width * 3 / 4`.
 *
 * 1152 is the largest width the render service reliably delivers as true 4:3:
 * it doesn't upscale past the source, so above 1152 a portrait photo degenerates
 * to a square (e.g. cc_ft_1536 -> 1152x1152), which pushes iMessage into its
 * compact card. 1152 stays landscape (1152x864) for every source.
 */
export const OG_IMAGE_WIDTH: FederatedPhotoWidth = 1152;

/**
 * Build a large, fixed-size Open Graph / Twitter image for a listing photo.
 * Returns the `cc_ft` render URL plus its exact pixel dimensions so
 * `og:image:width`/`og:image:height` are accurate. Returns null when the source
 * isn't a Federated Photo URL — the caller should fall back to the raw URL
 * (without dimensions).
 */
export function buildFederatedPhotoOgImage(
  apiUrl: string,
  width: FederatedPhotoWidth = OG_IMAGE_WIDTH,
): { url: string; width: number; height: number } | null {
  const info = extractFederatedPhotoInfoFromUrl(apiUrl);
  if (!info) return null;
  return {
    url: buildFederatedPhotoTreatmentUrl(info.photoKey, buildPresetTreatmentName('cc_ft', width), {
      origin: info.origin,
      ext: 'jpg',
    }),
    width,
    height: (width * 3) / 4,
  };
}

function buildFederatedPhotoUrl(
  photoKey: string,
  width: FederatedPhotoWidth,
  options: BuildFederatedPhotoUrlOptions = {},
): string {
  const { ext = 'webp', origin = FED_PHOTO_PROD_ORIGIN, treatment = 'cc_ft' } = options;
  const treatmentName = isCustomFederatedPhotoTreatment(treatment)
    ? treatment.defaultTreatment
    : buildPresetTreatmentName(treatment, width);

  return buildFederatedPhotoTreatmentUrl(photoKey, treatmentName, { ext, origin });
}

function buildSrcSet(
  photoKey: string,
  sizes: Array<FederatedPhotoWidth>,
  options: BuildFederatedPhotoUrlOptions = {},
): string {
  const { ext = 'webp', origin = FED_PHOTO_PROD_ORIGIN, treatment = 'cc_ft' } = options;
  if (isCustomFederatedPhotoTreatment(treatment)) {
    return (treatment.srcSetTreatments ?? [])
      .map(
        ({ width, treatment: treatmentName }) =>
          `${buildFederatedPhotoTreatmentUrl(photoKey, treatmentName, { ext, origin })} ${width}w`,
      )
      .join(', ');
  }

  return sizes.map((s) => `${buildFederatedPhotoUrl(photoKey, s, options)} ${s}w`).join(', ');
}

/**
 * Convert an API photo URL into responsive render attrs.
 *
 * @example
 *   const attrs = buildFederatedPhotoAttrs(
 *     'https://photos.zillowstatic.com/fp/4dab...-rentals_large.webp',
 *   );
 *   // → { photoKey, src: '...-cc_ft_768.webp', srcSet: '...384w, ...768w, ...', placeholder: '...-cc_ft_192.webp' }
 *
 * Returns `null` when the source URL is not a recognised Federated Photo render
 * host — callers should fall back to the original src in that case.
 */
export function buildFederatedPhotoAttrs(
  apiUrl: string,
  defaultSize: FederatedPhotoWidth = 768,
  srcSetSizes: Array<FederatedPhotoWidth> = [384, 768, 1152, 1536],
  options: BuildFederatedPhotoAttrsOptions = {},
): { photoKey: string; src: string; srcSet: string; placeholder: string } | null {
  const federatedPhoto = extractFederatedPhotoInfoFromUrl(apiUrl);
  if (!federatedPhoto) return null;
  const { ext = 'webp', treatment = 'cc_ft' } = options;
  const renderOptions = { ext, origin: federatedPhoto.origin, treatment };
  const placeholder = isCustomFederatedPhotoTreatment(treatment)
    ? buildFederatedPhotoTreatmentUrl(
        federatedPhoto.photoKey,
        treatment.placeholderTreatment ?? treatment.defaultTreatment,
        { ext, origin: federatedPhoto.origin },
      )
    : buildFederatedPhotoUrl(federatedPhoto.photoKey, 192, renderOptions);

  return {
    photoKey: federatedPhoto.photoKey,
    src: buildFederatedPhotoUrl(federatedPhoto.photoKey, defaultSize, renderOptions),
    srcSet: buildSrcSet(federatedPhoto.photoKey, srcSetSizes, renderOptions),
    placeholder,
  };
}
